import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, KeyRound, PencilLine, Settings as SettingsIcon } from 'lucide-react';
import {
  AI_PROVIDERS,
  AI_SETTINGS_CHANGED_EVENT,
  getAiSettings,
  setAiSettings,
  providerMeta,
  markApibooDefaultMigrated,
} from '../../services/ai/aiSettings';
import { hasAiKey, vaultKeyState } from '../../services/ai/keyStore';
import { getApibooStatus, peekApibooStatus } from '../../services/ai/apibooStatus';
import Tooltip from '../ui/Tooltip';
import './aiApiboo.css';

/**
 * Model switcher for the AI panel (17.08.2026).
 *
 * The same preference Settings → AI Chat writes (services/ai/aiSettings.js),
 * surfaced where the conversation is: switching mid-chat is safe because
 * engine.runAiTurn re-reads provider/model on EVERY turn and the history is
 * kept Anthropic-shaped for all adapters — the next turn simply goes to the
 * new model with the same conversation.
 *
 * Only providers the user actually has a key for are listed (owner decision
 * 16.09.2026) — a menu of models that cannot run is a menu of dead ends, and
 * the route to adding one is the "AI settings" footer.
 *
 * TWO entries survive that filter, and both have to:
 *   - the keyless Apiboo assistant, which needs no key at all;
 *   - the CURRENTLY SELECTED provider, even with its key gone. Hiding it would
 *     leave the trigger naming a model that is nowhere in the open menu, and
 *     the user unable to see what the next turn is about to use.
 * It also only applies once the vault has actually been READ: `keyed` is empty
 * while the vault is locked or still opening, and filtering on that would show
 * a user with three keys an empty menu.
 *
 * The keyless Apiboo entry (22.08.2026) is never probed for a key; its
 * subtitle comes from /ai/status and it is disabled when the server says
 * the assistant is unavailable.
 */
function AiModelPicker({ onOpenSettings }) {
  const [settings, setSettings] = useState(getAiSettings);
  const [open, setOpen] = useState(false);
  // provider id -> bool. Empty while unknown (vault locked/unavailable).
  const [keyed, setKeyed] = useState({});
  // Whether `keyed` is an ANSWER rather than an absence — see the filter below.
  const [probed, setProbed] = useState(false);
  const [apiboo, setApiboo] = useState(peekApibooStatus);
  // Provider id whose "Custom…" row is open, and the id being typed. Mirrors
  // Settings → AI Chat's Custom… escape hatch (providers retire ids between
  // our releases); the owner asked for it here too (22.08.2026).
  const [customFor, setCustomFor] = useState(null);
  const [customValue, setCustomValue] = useState('');
  const wrapRef = useRef(null);

  // Settings can also change from the Settings modal while the panel is open.
  useEffect(() => {
    const sync = () => setSettings(getAiSettings());
    window.addEventListener(AI_SETTINGS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AI_SETTINGS_CHANGED_EVENT, sync);
  }, []);

  // Only probe the vault when the menu opens — a locked vault leaves every
  // provider unmarked rather than falsely reading as "no key".
  useEffect(() => {
    if (!open) return undefined;
    let stale = false;
    getApibooStatus().then((s) => { if (!stale) setApiboo(s); });
    (async () => {
      if ((await vaultKeyState()) !== 'unlocked') return;
      const entries = await Promise.all(
        AI_PROVIDERS.filter((p) => !p.keyless).map(async (p) => {
          try {
            return [p.id, await hasAiKey(p.id)];
          } catch {
            // null, NOT false: "could not read" is not "has no key", and the
            // filter below treats the two very differently.
            return [p.id, null];
          }
        })
      );
      if (stale) return;
      const answered = entries.filter(([, v]) => v !== null);
      setKeyed(Object.fromEntries(answered));
      // Only hide anything once the vault answered for EVERY provider. The
      // vault auto-locks on idle, so it can lock between the `vaultKeyState`
      // check above and these reads; every one then throws, and treating that
      // as "no keys anywhere" would silently empty the menu for a user who has
      // three. Falling back to listing everything is the harmless direction.
      setProbed(answered.length === entries.length);
    })();
    return () => { stale = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') { setCustomFor(null); setOpen(false); } };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const pick = useCallback((provider, model) => {
    // A deliberate pick: the one-time default migration must never undo it.
    markApibooDefaultMigrated();
    setSettings(setAiSettings({ provider, model }));
    setCustomFor(null);
    setOpen(false);
  }, []);

  const openCustom = useCallback((providerId) => {
    setCustomFor(providerId);
    // Start from the current id when it already belongs to this provider, so
    // editing a typo is one keystroke, not a retype.
    setCustomValue(getAiSettings().provider === providerId ? getAiSettings().model : '');
  }, []);

  const commitCustom = useCallback((providerId) => {
    const model = customValue.trim();
    if (!model) return;
    pick(providerId, model);
  }, [customValue, pick]);

  const visibleProviders = probed
    ? AI_PROVIDERS.filter((p) => p.keyless || keyed[p.id] || settings.provider === p.id)
    : AI_PROVIDERS;

  return (
    <div className="ai-model-picker" ref={wrapRef}>
      <Tooltip side="top" content="Switch the model for the next turn">
        <button
          type="button"
          className="ai-model-picker__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Switch the model for the next turn"
          data-testid="ai-model-picker-trigger"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="ai-model-picker__name">
            {providerMeta(settings.provider).keyless ? providerMeta(settings.provider).label : settings.model}
          </span>
          <ChevronDown size={11} className="ai-model-picker__chevron" />
        </button>
      </Tooltip>

      {open && (
        <div className="ai-model-picker__menu" role="listbox" data-testid="ai-model-picker-menu">
          {visibleProviders.map((p) => (
            <div key={p.id} className="ai-model-picker__group">
              <div className="ai-model-picker__group-label">
                {p.label}
                {keyed[p.id] === false && (
                  // Only reachable for the selected provider now: its key was
                  // removed, and the row is kept so the trigger's model is
                  // visible in the menu.
                  <Tooltip side="top" content="No API key saved for this provider">
                    <span className="ai-model-picker__nokey" tabIndex={0}>
                      <KeyRound size={9} />
                      no key
                    </span>
                  </Tooltip>
                )}
              </div>
              {p.models.map((m) => {
                const active = settings.provider === p.id && settings.model === m;
                // Keyless: one entry, disabled while the server says no.
                const unavailable = Boolean(p.keyless && apiboo && !apiboo.available);
                return (
                  <button
                    key={m}
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={unavailable}
                    className={`ai-model-picker__item${active ? ' ai-model-picker__item--active' : ''}`}
                    onClick={() => pick(p.id, m)}
                    data-testid={p.keyless ? 'ai-model-picker-apiboo' : undefined}
                  >
                    <Check size={11} className="ai-model-picker__check" style={{ opacity: active ? 1 : 0 }} />
                    <span className="truncate">{p.keyless ? p.label : m}</span>
                    {p.keyless && (
                      <span className="ai-model-picker__sub truncate">
                        {unavailable
                          ? 'unavailable'
                          : `${apiboo?.model || 'server model'} · included in your plan`}
                      </span>
                    )}
                  </button>
                );
              })}
              {!p.keyless && settings.provider === p.id && !p.models.includes(settings.model) && (
                // A custom id in use: show it as the active row so the user
                // sees what the next turn goes to, not an unselected list.
                <button
                  type="button"
                  role="option"
                  aria-selected
                  className="ai-model-picker__item ai-model-picker__item--active"
                  onClick={() => pick(p.id, settings.model)}
                  data-testid={`ai-model-picker-custom-active-${p.id}`}
                >
                  <Check size={11} className="ai-model-picker__check" />
                  <span className="truncate">{settings.model}</span>
                </button>
              )}
              {!p.keyless && customFor !== p.id && (
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="ai-model-picker__item ai-model-picker__item--custom"
                  onClick={() => openCustom(p.id)}
                  data-testid={`ai-model-picker-custom-${p.id}`}
                >
                  <PencilLine size={11} className="ai-model-picker__check" />
                  <span className="truncate">Custom…</span>
                </button>
              )}
              {!p.keyless && customFor === p.id && (
                <input
                  type="text"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  className="ai-model-picker__custom-input"
                  placeholder={`model id, e.g. ${p.models[0]}`}
                  aria-label={`Custom ${p.label} model`}
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitCustom(p.id); }
                    if (e.key === 'Escape') { e.stopPropagation(); setCustomFor(null); }
                  }}
                  data-testid={`ai-model-picker-custom-input-${p.id}`}
                />
              )}
            </div>
          ))}
          {onOpenSettings && (
            <button
              type="button"
              className="ai-model-picker__footer"
              onClick={() => { setOpen(false); onOpenSettings(); }}
            >
              <SettingsIcon size={11} />
              AI settings
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default AiModelPicker;
